import {
  createContext,
  startTransition,
  useContext,
  useDeferredValue,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
} from "react";
import { NativeSelect, TextInput } from "@mantine/core";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createBrowserRouter, RouterProvider, useLocation, useNavigate, useParams } from "react-router-dom";

import { fetchJson, installEasyAppBridge, readSessionJson, resolveMountedBasename, writeSessionJson } from "@easy-core";
import { EditorPage, EditorHeader, EditorPanel, ImportColumnMapping, AppActionBar, AppChrome, AppSecondaryBar, AppSurface, PanelMessage } from "@easy-ui";

import "@easy-ui/legacy/appBase.css";
import "../../event-planner/src/eventPlanner.css";
import "./contacts.css";
import { ContactsIcon, ContactsIconLabel as IconLabel } from "./contactsIcons";
import {
  apiUrl,
  buildContactPayload,
  buildGroupPayload,
  buildImportPayload,
  contactMatchesFilters,
  createDefaultContactsFilters,
  createDefaultGroupFilters,
  createEmptyContactForm,
  createEmptyGroupForm,
  downloadImportTemplate,
  findDuplicateEmails,
  formatContactName,
  formatContactSource,
  formatMemberSearch,
  groupMatchesFilters,
  hasFieldError,
  inferContactFieldErrors,
  inferGroupFieldErrors,
  normalizeApiError,
  parseImportFile,
  importFields,
  resolveFieldErrors,
  sortContacts,
  sortGroups,
  statusClassName,
  toContactForm,
  toGroupForm,
  type ContactRecord,
  type ContactsFilters,
  type ContactsImportPreview,
  type ContactsMeta,
  type GroupFilters,
  type GroupRecord,
  type StatusState,
} from "./contactsUtils";

function SelectionCheckbox({
  checked,
  indeterminate = false,
  onChange,
  ariaLabel,
}: {
  checked: boolean;
  indeterminate?: boolean;
  onChange: (checked: boolean) => void;
  ariaLabel: string;
}) {
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.indeterminate = indeterminate;
    }
  }, [indeterminate]);

  return (
    <input
      ref={inputRef}
      type="checkbox"
      checked={checked}
      aria-label={ariaLabel}
      onChange={(event) => onChange(event.target.checked)}
      onClick={(event) => event.stopPropagation()}
    />
  );
}

function useContactsData() {
  const metaQuery = useQuery({
    queryKey: ["contacts-meta"],
    queryFn: () => fetchJson<ContactsMeta>(apiUrl("meta")),
  });
  const contactsQuery = useQuery({
    queryKey: ["contacts-contacts"],
    queryFn: () => fetchJson<ContactRecord[]>(apiUrl("contacts")),
  });
  const groupsQuery = useQuery({
    queryKey: ["contacts-groups"],
    queryFn: () => fetchJson<GroupRecord[]>(apiUrl("groups")),
  });

  const contacts = useMemo(() => sortContacts(contactsQuery.data || []), [contactsQuery.data]);
  const groups = useMemo(() => sortGroups(groupsQuery.data || []), [groupsQuery.data]);

  return {
    metaQuery,
    contactsQuery,
    groupsQuery,
    contacts,
    groups,
  };
}

type ContactsListViewState = {
  filters: ContactsFilters;
  isFiltersPanelOpen: boolean;
  isImportModalOpen: boolean;
  isBulkGroupModalOpen: boolean;
  selectedContactIds: number[];
};

type GroupsListViewState = {
  filters: GroupFilters;
  isFiltersPanelOpen: boolean;
  selectedGroupIds: number[];
};

type ContactEditorViewState = {
  isEditingDetail: boolean;
};

type GroupEditorViewState = {
  isEditingDetail: boolean;
};

type ContactsRouteConfig = {
  listPath: string;
  contactCreatePath: string;
  contactDetailPattern: string;
  contactDetailPath: (contactId: number | string) => string;
  groupsListPath: string;
  groupCreatePath: string;
  groupDetailPattern: string;
  groupDetailPath: (groupId: number | string) => string;
};

const CONTACTS_LIST_VIEW_STATE_STORAGE_KEY = "easy-contacts:list-view";
const GROUPS_LIST_VIEW_STATE_STORAGE_KEY = "easy-contacts:groups-view";

const CONTACTS_STANDALONE_ROUTES: ContactsRouteConfig = {
  listPath: "/",
  contactCreatePath: "/contacts/new",
  contactDetailPattern: "/contacts/:contactId",
  contactDetailPath: (contactId) => `/contacts/${contactId}`,
  groupsListPath: "/groups",
  groupCreatePath: "/groups/new",
  groupDetailPattern: "/groups/:groupId",
  groupDetailPath: (groupId) => `/groups/${groupId}`,
};

const CONTACTS_HOSTED_ROUTES: ContactsRouteConfig = {
  listPath: "/contacts",
  contactCreatePath: "/contacts/new",
  contactDetailPattern: "/contacts/:contactId",
  contactDetailPath: (contactId) => `/contacts/${contactId}`,
  groupsListPath: "/contacts/groups",
  groupCreatePath: "/contacts/groups/new",
  groupDetailPattern: "/contacts/groups/:groupId",
  groupDetailPath: (groupId) => `/contacts/groups/${groupId}`,
};

const ContactsRouteConfigContext = createContext<ContactsRouteConfig>(CONTACTS_STANDALONE_ROUTES);

function useContactsRoutes() {
  return useContext(ContactsRouteConfigContext);
}

function openSelectFromFieldClick(event: ReactMouseEvent<HTMLLabelElement>) {
  if (event.target instanceof HTMLSelectElement) {
    return;
  }

  const select = event.currentTarget.querySelector("select");
  if (!select) {
    return;
  }

  select.focus();
  try {
    select.showPicker();
  } catch {
    // showPicker is unavailable in some browsers or blocked outside direct user activation.
  }
}

function withContactsRouteConfig(element: ReactNode, routes: ContactsRouteConfig) {
  return <ContactsRouteConfigContext.Provider value={routes}>{element}</ContactsRouteConfigContext.Provider>;
}

function ModalShell({
  isOpen,
  title,
  subtitle,
  onClose,
  children,
}: {
  isOpen: boolean;
  title: string;
  subtitle?: string;
  onClose: () => void;
  children: ReactNode;
}) {
  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [isOpen, onClose]);

  if (!isOpen) {
    return null;
  }

  return (
    <div className="event-modal-backdrop" onClick={onClose}>
      <div className="event-modal" role="dialog" aria-label={title} aria-modal="true" onClick={(event) => event.stopPropagation()}>
        <div className="event-modal-head">
          <div>
            <p className="section-kicker">Action</p>
            <h2>{title}</h2>
            {subtitle ? <p className="muted">{subtitle}</p> : null}
          </div>
          <button type="button" className="button" onClick={onClose}>
            Fermer
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function ContactsLayoutFrame({
  subtitle,
  actions,
  editorBack,
  catalogActions,
  heading,
  children,
}: {
  subtitle: string;
  actions?: ReactNode;
  editorBack?: ReactNode;
  catalogActions?: ReactNode;
  heading?: ReactNode;
  children: ReactNode;
}) {
  const routes = useContactsRoutes();
  const navigate = useNavigate();
  const location = useLocation();
  const isGroupsView =
    location.pathname === routes.groupsListPath || location.pathname.startsWith(`${routes.groupsListPath}/`);

  useEffect(() => {
    document.title = "Relyo · Contacts";
    installEasyAppBridge("contacts");
  }, []);

  if (editorBack) {
    return <EditorPage className="contacts-editor-shell" back={editorBack} actions={actions} heading={heading}>{children}</EditorPage>;
  }

  return (
    <AppChrome
      className="contacts-page-shell"
      title={isGroupsView ? "Groupes" : "Contacts"}
      headerActions={catalogActions}
      topBarStart={
        <div className="event-view-toggle" role="group" aria-label="Choix de la vue">
          <button
            type="button"
            className={`event-view-toggle-button${!isGroupsView ? " is-active" : ""}`}
            aria-pressed={!isGroupsView}
            onClick={() => startTransition(() => navigate(routes.listPath))}
          >
            <IconLabel icon="address-book" label="Contacts" />
          </button>
          <button
            type="button"
            className={`event-view-toggle-button${isGroupsView ? " is-active" : ""}`}
            aria-pressed={isGroupsView}
            onClick={() => startTransition(() => navigate(routes.groupsListPath))}
          >
            <IconLabel icon="folders" label="Groupes" />
          </button>
        </div>
      }
      topBarEnd={actions}
    >
      {children}
    </AppChrome>
  );
}

function ContactsStats({ contacts, groups }: { contacts: ContactRecord[]; groups: GroupRecord[] }) {
  const groupedContactsCount = contacts.filter((contact) => contact.groups.length > 0).length;
  const importedContactsCount = contacts.filter((contact) => contact.source !== "manual").length;

  return (
    <div className="event-stats-grid">
      <article className="event-stat-card">
        <span className="event-stat-label">
          <ContactsIcon name="users" className="event-stat-icon" />
          Contacts
        </span>
        <strong>{contacts.length}</strong>
        <span className="muted">Base disponible pour vos newsletters.</span>
      </article>
      <article className="event-stat-card">
        <span className="event-stat-label">
          <ContactsIcon name="folders" className="event-stat-icon" />
          Groupes
        </span>
        <strong>{groups.length}</strong>
        <span className="muted">Segmentez vos audiences par usage.</span>
      </article>
      <article className="event-stat-card">
        <span className="event-stat-label">
          <ContactsIcon name="upload" className="event-stat-icon" />
          Imports
        </span>
        <strong>{importedContactsCount}</strong>
        <span className="muted">
          {groupedContactsCount} contact{groupedContactsCount > 1 ? "s" : ""} déjà reliés à un groupe.
        </span>
      </article>
    </div>
  );
}

function formatDraftContactName(form: { first_name: string; last_name: string; email: string }) {
  return formatContactName({
    full_name: "",
    first_name: form.first_name,
    last_name: form.last_name,
    email: form.email,
  });
}

function publishContactsWorkspaceSync(reason: string, groups: GroupRecord[] = []) {
  window.EasyAppBridge?.publish("contacts.groups.updated", {
    reason,
    groupIds: groups.map((group) => group.id),
    groupNames: groups.map((group) => group.name),
  });
}

function formatContactBounceDate(bouncedAt: string | null) {
  if (!bouncedAt) {
    return "";
  }

  const parsed = new Date(bouncedAt);
  if (Number.isNaN(parsed.getTime())) {
    return bouncedAt.replace("T", " ");
  }

  return new Intl.DateTimeFormat("fr-FR", {
    dateStyle: "long",
    timeStyle: "short",
  }).format(parsed);
}

function countActiveContactFilters(filters: ContactsFilters) {
  return [
    filters.search.trim().length > 0,
    filters.group_id !== "all",
    filters.source !== "all",
    filters.city !== "all",
    filters.email_quality !== "all",
  ].filter(Boolean).length;
}

function countActiveGroupFilters(filters: GroupFilters) {
  return [filters.search.trim().length > 0].filter(Boolean).length;
}

function resolveContactFilterLabel(
  filters: ContactsFilters,
  groups: GroupRecord[],
  sources: string[],
  cities: string[]
) {
  const chips: { key: string; label: string }[] = [];

  if (filters.search.trim()) {
    chips.push({ key: "search", label: `Recherche: ${filters.search.trim()}` });
  }

  if (filters.group_id !== "all") {
    const group = groups.find((entry) => entry.id === filters.group_id);
    chips.push({ key: "group_id", label: `Groupe: ${group?.name || "Inconnu"}` });
  }

  if (filters.city !== "all") {
    const cityLabel = cities.find((city) => city === filters.city) || filters.city;
    chips.push({ key: "city", label: `Ville: ${cityLabel}` });
  }

  if (filters.source !== "all") {
    const sourceLabel = sources.find((source) => source === filters.source) || filters.source;
    chips.push({ key: "source", label: `Source: ${formatContactSource(sourceLabel)}` });
  }

  if (filters.email_quality !== "all") {
    const labels = { duplicates: "Doublons", bounced: "Bounces", missing: "Emails manquants", unsubscribed: "Désabonnés", paused: "Newsletters en pause", awaiting: "Confirmation en cours" };
    chips.push({ key: "email_quality", label: labels[filters.email_quality] });
  }

  return chips;
}

type BulkGroupMode = "existing" | "new";

function ContactsFiltersBar({
  filters,
  groups,
  sources,
  cities,
  onChange,
  onReset,
}: {
  filters: ContactsFilters;
  groups: GroupRecord[];
  sources: string[];
  cities: string[];
  onChange: (patch: Partial<ContactsFilters>) => void;
  onReset: () => void;
}) {
  const activeFiltersCount = countActiveContactFilters(filters);

  return (
    <div className={`event-filter-bar${activeFiltersCount > 0 ? " has-active-filters" : ""}`}>
      <button
        type="button"
        className="event-filter-reset-inline"
        aria-label={activeFiltersCount > 0 ? "Réinitialiser les filtres" : "Aucun filtre actif"}
        title={activeFiltersCount > 0 ? "Réinitialiser les filtres" : "Aucun filtre actif"}
        onClick={onReset}
        disabled={activeFiltersCount === 0}
      >
        <ContactsIcon name="x" className="ui-icon" />
      </button>
      <div className="event-filter-grid contacts-filter-grid">
        <TextInput
          type="search"
          label={
            <span className="event-filter-label">
              <ContactsIcon name="search" className="ui-icon event-filter-symbol" />
              Recherche
            </span>
          }
          className="event-filter-field event-filter-field-search"
          placeholder="Nom, email, ville, adresse, societe..."
          value={filters.search}
          leftSection={<ContactsIcon name="search" className="ui-icon event-filter-input-icon" />}
          onChange={(event) => onChange({ search: event.currentTarget.value })}
        />

        <NativeSelect
          label={
            <span className="event-filter-label">
              <ContactsIcon name="folders" className="ui-icon event-filter-symbol" />
              Groupe
            </span>
          }
          className="event-filter-field"
          value={String(filters.group_id)}
          data={[
            { value: "all", label: "Tous les groupes" },
            ...groups.map((group) => ({ value: String(group.id), label: group.name })),
          ]}
          onChange={(event) =>
            onChange({
              group_id: event.currentTarget.value === "all" ? "all" : Number(event.currentTarget.value),
            })
          }
        />

        <NativeSelect
          label={
            <span className="event-filter-label">
              <ContactsIcon name="map-pin" className="ui-icon event-filter-symbol" />
              Ville
            </span>
          }
          className="event-filter-field"
          value={String(filters.city)}
          data={[
            { value: "all", label: "Toutes les villes" },
            ...cities.map((city) => ({ value: city, label: city })),
          ]}
          onChange={(event) => onChange({ city: event.currentTarget.value })}
        />

        <NativeSelect
          label={
            <span className="event-filter-label">
              <ContactsIcon name="upload" className="ui-icon event-filter-symbol" />
              Source
            </span>
          }
          className="event-filter-field"
          value={filters.source}
          data={[
            { value: "all", label: "Toutes les sources" },
            ...sources.map((source) => ({ value: source, label: formatContactSource(source) })),
          ]}
          onChange={(event) => onChange({ source: event.currentTarget.value })}
        />
      </div>
    </div>
  );
}

function GroupsFiltersBar({
  filters,
  onChange,
  onReset,
}: {
  filters: GroupFilters;
  onChange: (patch: Partial<GroupFilters>) => void;
  onReset: () => void;
}) {
  const activeFiltersCount = countActiveGroupFilters(filters);

  return (
    <div className={`event-filter-bar${activeFiltersCount > 0 ? " has-active-filters" : ""}`}>
      <button
        type="button"
        className="event-filter-reset-inline"
        aria-label={activeFiltersCount > 0 ? "Réinitialiser la recherche" : "Aucune recherche active"}
        title={activeFiltersCount > 0 ? "Réinitialiser la recherche" : "Aucune recherche active"}
        onClick={onReset}
        disabled={activeFiltersCount === 0}
      >
        <ContactsIcon name="x" className="ui-icon" />
      </button>
      <div className="event-filter-grid is-compact">
        <TextInput
          type="search"
          label={
            <span className="event-filter-label">
              <ContactsIcon name="search" className="ui-icon event-filter-symbol" />
              Recherche
            </span>
          }
          className="event-filter-field event-filter-field-search"
          placeholder="Nom du groupe, description, membre..."
          value={filters.search}
          leftSection={<ContactsIcon name="search" className="ui-icon event-filter-input-icon" />}
          onChange={(event) => onChange({ search: event.currentTarget.value })}
        />
      </div>
    </div>
  );
}

function ContactsListPage() {
  const routes = useContactsRoutes();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const initialViewState = readSessionJson<ContactsListViewState>(CONTACTS_LIST_VIEW_STATE_STORAGE_KEY, {
    filters: createDefaultContactsFilters(),
    isFiltersPanelOpen: false,
    isImportModalOpen: false,
    isBulkGroupModalOpen: false,
    selectedContactIds: [],
  });
  const { metaQuery, contactsQuery, groupsQuery, contacts, groups } = useContactsData();
  const [filters, setFilters] = useState<ContactsFilters>(() => ({
    ...createDefaultContactsFilters(),
    ...initialViewState.filters,
    email_quality: initialViewState.filters.email_quality ??
      ((initialViewState.filters as ContactsFilters & { duplicates_only?: boolean }).duplicates_only ? "duplicates" : "all"),
  }));
  const [isFiltersPanelOpen, setIsFiltersPanelOpen] = useState(initialViewState.isFiltersPanelOpen ?? false);
  const [collectionStatus, setCollectionStatus] = useState<StatusState>({ message: "", tone: "neutral" });
  const [isImportModalOpen, setIsImportModalOpen] = useState(initialViewState.isImportModalOpen);
  const [isBulkGroupModalOpen, setIsBulkGroupModalOpen] = useState(initialViewState.isBulkGroupModalOpen);
  const [isParsingImport, setIsParsingImport] = useState(false);
  const [importPreview, setImportPreview] = useState<ContactsImportPreview | null>(null);
  const [importFileName, setImportFileName] = useState("");
  const [importFile, setImportFile] = useState<File | null>(null);
  const [isMappingConfirmed, setIsMappingConfirmed] = useState(false);
  const [importMappingError, setImportMappingError] = useState("");
  const [selectedContactIds, setSelectedContactIds] = useState<number[]>(initialViewState.selectedContactIds);
  const [bulkGroupMode, setBulkGroupMode] = useState<BulkGroupMode>("existing");
  const [selectedGroupId, setSelectedGroupId] = useState<string>("all");
  const [newGroupForm, setNewGroupForm] = useState(createEmptyGroupForm());
  const [bulkGroupFieldErrors, setBulkGroupFieldErrors] = useState<string[]>([]);
  const [bulkGroupStatus, setBulkGroupStatus] = useState<StatusState>({ message: "", tone: "neutral" });
  const importInputRef = useRef<HTMLInputElement | null>(null);
  const deferredSearch = useDeferredValue(filters.search);
  const effectiveFilters = useMemo(() => ({ ...filters, search: deferredSearch }), [filters, deferredSearch]);
  const duplicateEmails = useMemo(() => findDuplicateEmails(contacts), [contacts]);
  const filteredContacts = useMemo(
    () => contacts.filter((contact) => contactMatchesFilters(contact, effectiveFilters, duplicateEmails)),
    [contacts, duplicateEmails, effectiveFilters]
  );
  const availableSources = useMemo(
    () => Array.from(new Set(contacts.map((contact) => contact.source).filter(Boolean))).sort(),
    [contacts]
  );
  const availableCities = useMemo(
    () => Array.from(new Set(contacts.map((contact) => contact.city.trim()).filter(Boolean))).sort((left, right) => left.localeCompare(right, "fr", { sensitivity: "base" })),
    [contacts]
  );
  const filteredContactIds = useMemo(() => filteredContacts.map((contact) => contact.id), [filteredContacts]);
  const activeFiltersCount = useMemo(() => countActiveContactFilters(filters), [filters]);
  const activeFilterChips = useMemo(
    () => resolveContactFilterLabel(filters, groups, availableSources, availableCities),
    [filters, groups, availableSources, availableCities]
  );
  const selectedContacts = useMemo(
    () => contacts.filter((contact) => selectedContactIds.includes(contact.id)),
    [contacts, selectedContactIds]
  );
  const selectedContactsCount = selectedContacts.length;
  const showContactsBlankState = !contactsQuery.isLoading && contacts.length === 0;
  const allFilteredSelected =
    filteredContactIds.length > 0 && filteredContactIds.every((contactId) => selectedContactIds.includes(contactId));
  const someFilteredSelected = filteredContactIds.some((contactId) => selectedContactIds.includes(contactId));

  useEffect(() => {
    const knownContactIds = new Set(contacts.map((contact) => contact.id));
    setSelectedContactIds((current) => current.filter((contactId) => knownContactIds.has(contactId)));
  }, [contacts]);

  useEffect(() => {
    writeSessionJson(CONTACTS_LIST_VIEW_STATE_STORAGE_KEY, {
      filters,
      isFiltersPanelOpen,
      isImportModalOpen,
      isBulkGroupModalOpen,
      selectedContactIds,
    } satisfies ContactsListViewState);
  }, [filters, isBulkGroupModalOpen, isFiltersPanelOpen, isImportModalOpen, selectedContactIds]);

  const importMutation = useMutation({
    mutationFn: async () => {
      if (!importPreview) {
        return { message: "", created_count: 0, updated_count: 0, imported_count: 0 };
      }
      return fetchJson<{ message: string; created_count: number; updated_count: number; imported_count: number }>(
        apiUrl("import"),
        {
          method: "POST",
          body: JSON.stringify(buildImportPayload(importPreview)),
        }
      );
    },
    onSuccess: async (result) => {
      setCollectionStatus({
        message: result.message || "Import termine.",
        tone: "success",
      });
      setImportPreview(null);
      setImportFileName("");
      setImportFile(null);
      setImportMappingError("");
      setIsImportModalOpen(false);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["contacts-contacts"] }),
        queryClient.invalidateQueries({ queryKey: ["contacts-groups"] }),
        queryClient.invalidateQueries({ queryKey: ["contacts-meta"] }),
      ]);
      publishContactsWorkspaceSync("import");
    },
    onError: (error) => {
      const normalizedError = normalizeApiError(error);
      setCollectionStatus({ message: normalizedError.message, tone: "error" });
    },
  });

  const assignContactsToGroupMutation = useMutation({
    mutationFn: async () => {
      const normalizedSelection = Array.from(new Set(selectedContactIds)).filter((contactId) => contactId > 0);
      if (normalizedSelection.length === 0) {
        throw new Error("Sélectionnez au moins un contact.");
      }

      if (bulkGroupMode === "existing") {
        const groupId = Number(selectedGroupId);
        const existingGroup = groups.find((group) => group.id === groupId);
        if (!existingGroup) {
          throw new Error("Choisissez un groupe existant.");
        }

        return fetchJson<GroupRecord>(apiUrl("groups"), {
          method: "POST",
          body: JSON.stringify(
            buildGroupPayload({
              id: existingGroup.id,
              name: existingGroup.name,
              description: existingGroup.description,
              color: existingGroup.color,
              member_ids: Array.from(new Set([...existingGroup.members.map((member) => member.id), ...normalizedSelection])),
            })
          ),
        });
      }

      return fetchJson<GroupRecord>(apiUrl("groups"), {
        method: "POST",
        body: JSON.stringify(
          buildGroupPayload({
            ...newGroupForm,
            member_ids: normalizedSelection,
          })
        ),
      });
    },
    onMutate: () => {
      setBulkGroupFieldErrors([]);
      setBulkGroupStatus({ message: "Mise a jour du groupe en cours...", tone: "neutral" });
    },
    onSuccess: async (group) => {
      setBulkGroupStatus({
        message: `${selectedContactsCount} contact${selectedContactsCount > 1 ? "s" : ""} ajoute${selectedContactsCount > 1 ? "s" : ""} au groupe ${group.name}.`,
        tone: "success",
      });
      setCollectionStatus({
        message: `${selectedContactsCount} contact${selectedContactsCount > 1 ? "s" : ""} ajoute${selectedContactsCount > 1 ? "s" : ""} au groupe ${group.name}.`,
        tone: "success",
      });
      setIsBulkGroupModalOpen(false);
      setSelectedContactIds([]);
      setBulkGroupMode("existing");
      setSelectedGroupId("all");
      setNewGroupForm(createEmptyGroupForm());
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["contacts-contacts"] }),
        queryClient.invalidateQueries({ queryKey: ["contacts-groups"] }),
      ]);
      publishContactsWorkspaceSync("bulk-group-assign", [group]);
    },
    onError: (error) => {
      const normalizedError = normalizeApiError(error);
      setBulkGroupFieldErrors(resolveFieldErrors(normalizedError, inferGroupFieldErrors(normalizedError.message)));
      setBulkGroupStatus({ message: normalizedError.message, tone: "error" });
    },
  });

  const bulkDeleteMutation = useMutation({
    mutationFn: () =>
      fetchJson<{ deleted_count: number }>(apiUrl("contacts/delete-bulk"), {
        method: "POST",
        body: JSON.stringify({ contact_ids: selectedContactIds }),
      }),
    onMutate: () => {
      setCollectionStatus({ message: "Suppression des contacts en cours...", tone: "neutral" });
    },
    onSuccess: async ({ deleted_count }) => {
      setSelectedContactIds([]);
      setCollectionStatus({
        message: `${deleted_count} contact${deleted_count > 1 ? "s" : ""} supprimé${deleted_count > 1 ? "s" : ""}.`,
        tone: "success",
      });
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["contacts-contacts"] }),
        queryClient.invalidateQueries({ queryKey: ["contacts-groups"] }),
        queryClient.invalidateQueries({ queryKey: ["contacts-meta"] }),
      ]);
      publishContactsWorkspaceSync("contact-deleted");
    },
    onError: (error) => {
      const normalizedError = normalizeApiError(error);
      setCollectionStatus({ message: normalizedError.message, tone: "error" });
    },
  });

  async function applyImportMapping(mapping: string[]) {
    if (!importFile) return;
    setIsParsingImport(true);
    setImportMappingError("");
    try {
      const preview = await parseImportFile(importFile, contacts, mapping);
      setImportPreview(preview);
      setIsMappingConfirmed(true);
      setCollectionStatus({ message: `${preview.readyCount} ligne(s) prête(s), ${preview.issueCount} avec alerte.`, tone: preview.issueCount > 0 ? "error" : "success" });
    } catch (error) {
      setImportMappingError(error instanceof Error ? error.message : "Impossible de prévisualiser le fichier.");
    } finally {
      setIsParsingImport(false);
    }
  }

  async function handleImportFileChange(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    event.target.value = "";

    if (!file) {
      return;
    }

    setImportFile(file);
    setIsMappingConfirmed(false);
    setImportMappingError("");
    setImportPreview(null);
    setImportFileName(file.name);
    setIsParsingImport(true);
    setCollectionStatus({ message: "Analyse du fichier en cours...", tone: "neutral" });
    try {
      const preview = await parseImportFile(file, contacts);
      setIsMappingConfirmed(!preview.columns);
      setImportPreview(preview);
      setCollectionStatus({
        message: `${preview.readyCount} ligne(s) prete(s), ${preview.issueCount} avec alerte.`,
        tone: preview.issueCount > 0 ? "error" : "success",
      });
      setIsImportModalOpen(true);
    } catch (error) {
      setImportPreview(null);
      setImportMappingError(error instanceof Error ? error.message : "Impossible de lire le fichier.");
      setCollectionStatus({
        message: error instanceof Error ? error.message : "Impossible de lire le fichier d'import.",
        tone: "error",
      });
    } finally {
      setIsParsingImport(false);
    }
  }

  function toggleContactSelection(contactId: number, checked: boolean) {
    setSelectedContactIds((current) => {
      if (checked) {
        return current.includes(contactId) ? current : [...current, contactId];
      }
      return current.filter((value) => value !== contactId);
    });
  }

  function toggleSelectAllFilteredContacts(checked: boolean) {
    setSelectedContactIds((current) => {
      if (checked) {
        return Array.from(new Set([...current, ...filteredContactIds]));
      }
      const filteredSet = new Set(filteredContactIds);
      return current.filter((contactId) => !filteredSet.has(contactId));
    });
  }

  function openBulkGroupModal(mode: BulkGroupMode) {
    setBulkGroupMode(mode);
    setBulkGroupFieldErrors([]);
    setBulkGroupStatus({ message: "", tone: "neutral" });
    if (mode === "existing") {
      setSelectedGroupId(groups[0] ? String(groups[0].id) : "all");
    } else {
      setNewGroupForm(createEmptyGroupForm());
    }
    setIsBulkGroupModalOpen(true);
  }

  function clearFilterChip(key: string) {
    setFilters((current) => {
      if (key === "search") {
        return { ...current, search: "" };
      }
      if (key === "group_id") {
        return { ...current, group_id: "all" };
      }
      if (key === "city") {
        return { ...current, city: "all" };
      }
      if (key === "source") {
        return { ...current, source: "all" };
      }
      if (key === "email_quality") {
        return { ...current, email_quality: "all" };
      }
      return current;
    });
  }

  const error =
    metaQuery.error instanceof Error
      ? metaQuery.error
      : contactsQuery.error instanceof Error
        ? contactsQuery.error
        : groupsQuery.error instanceof Error
          ? groupsQuery.error
          : null;

  return (
    <ContactsLayoutFrame
      subtitle="Base hosted de contacts et de groupes pour la newsletter."
      catalogActions={<div className="app-catalog-actions">
                  <button type="button" className="button app-catalog-secondary-button" onClick={() => setIsImportModalOpen(true)}>
                    <IconLabel icon="upload" label="Importer" />
                  </button>
                  <button type="button" className="button app-catalog-secondary-button" onClick={() => startTransition(() => navigate(routes.groupCreatePath))}>
                    <IconLabel icon="folder" label="Nouveau groupe" />
                  </button>
                  <button type="button" className="button button-primary" onClick={() => startTransition(() => navigate(routes.contactCreatePath))}>
                    <IconLabel icon="plus" label="Nouveau contact" />
                  </button>
                </div>}
    >
      <input
        ref={importInputRef}
        type="file"
        hidden={true}
        accept=".csv,.xlsx,.vcf,text/csv,text/vcard,text/x-vcard"
        disabled={isParsingImport || importMutation.isPending}
        onChange={handleImportFileChange}
      />

      {error ? (
        <PanelMessage title="Chargement impossible" message={error.message} />
      ) : (
        <>
          <AppSurface className="event-list-panel">
            <div className="app-catalog-toolbar">
              <div className="app-catalog-toolbar-main">
                <div className="app-catalog-search-shell contacts-catalog-search-shell">
                  <div className="app-catalog-searchbar">
                    <ContactsIcon name="search" className="ui-icon" />
                    <input
                      type="search"
                      value={filters.search}
                      placeholder="Nom, email, ville, adresse, societe..."
                      aria-label="Rechercher des contacts"
                      onChange={(event) => {
                        const value = event.currentTarget.value;
                        setFilters((current) => ({ ...current, search: value }));
                      }}
                    />
                    {filters.search.trim() ? (
                      <button
                        type="button"
                        className="app-catalog-clear-search"
                        aria-label="Effacer la recherche"
                        title="Effacer la recherche"
                        onClick={() => setFilters((current) => ({ ...current, search: "" }))}
                      >
                        <ContactsIcon name="x" className="ui-icon" />
                      </button>
                    ) : null}
                  </div>
                  <button
                    type="button"
                    className={`app-catalog-filter-toggle${activeFiltersCount > 0 ? " has-active-filters" : ""}${isFiltersPanelOpen ? " is-open" : ""}`}
                    aria-expanded={isFiltersPanelOpen}
                    aria-controls="contacts-filters-panel"
                    onClick={() => setIsFiltersPanelOpen((current) => !current)}
                  >
                    <ContactsIcon name="filter" className="ui-icon" />
                    <span>Filtres</span>
                    {activeFiltersCount > 0 ? <span className="app-catalog-filter-count">{activeFiltersCount}</span> : null}
                  </button>
                </div>


              </div>

              {isFiltersPanelOpen ? (
                <div id="contacts-filters-panel" className="app-catalog-advanced-filters">
                  <div className="app-catalog-advanced-grid is-two-columns">
                    <label className="app-catalog-advanced-field" onClick={openSelectFromFieldClick}>
                      <span className="app-catalog-field-label">
                        <ContactsIcon name="folders" className="ui-icon" />
                        Groupe
                      </span>
                      <div className="toolbar-dropdown">
                        <select
                          className="toolbar-select"
                          value={String(filters.group_id)}
                          onChange={(event) => {
                            const value = event.currentTarget.value;
                            setFilters((current) => ({
                              ...current,
                              group_id: value === "all" ? "all" : Number(value),
                            }));
                          }}
                        >
                          <option value="all">Tous les groupes</option>
                          {groups.map((group) => (
                            <option key={group.id} value={group.id}>
                              {group.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    </label>

                    <label className="app-catalog-advanced-field" onClick={openSelectFromFieldClick}>
                      <span className="app-catalog-field-label">
                        <ContactsIcon name="map-pin" className="ui-icon" />
                        Ville
                      </span>
                      <div className="toolbar-dropdown">
                        <select
                          className="toolbar-select"
                          value={String(filters.city)}
                          onChange={(event) => {
                            const value = event.currentTarget.value;
                            setFilters((current) => ({ ...current, city: value }));
                          }}
                        >
                          <option value="all">Toutes les villes</option>
                          {availableCities.map((city) => (
                            <option key={city} value={city}>
                              {city}
                            </option>
                          ))}
                        </select>
                      </div>
                    </label>

                    <label className="app-catalog-advanced-field" onClick={openSelectFromFieldClick}>
                      <span className="app-catalog-field-label">
                        <ContactsIcon name="upload" className="ui-icon" />
                        Source
                      </span>
                      <div className="toolbar-dropdown">
                        <select
                          className="toolbar-select"
                          value={filters.source}
                          onChange={(event) => {
                            const value = event.currentTarget.value;
                            setFilters((current) => ({ ...current, source: value }));
                          }}
                        >
                          <option value="all">Toutes les sources</option>
                          {availableSources.map((source) => (
                            <option key={source} value={source}>
                              {formatContactSource(source)}
                            </option>
                          ))}
                        </select>
                      </div>
                    </label>

                    <label className="app-catalog-advanced-field" onClick={openSelectFromFieldClick}>
                      <span className="app-catalog-field-label">
                        <ContactsIcon name="mail" className="ui-icon" />
                        Qualité d'email
                      </span>
                      <div className="toolbar-dropdown">
                        <select
                          className="toolbar-select"
                          value={filters.email_quality}
                          onChange={(event) => {
                            const value = event.currentTarget.value as ContactsFilters["email_quality"];
                            setFilters((current) => ({ ...current, email_quality: value }));
                          }}
                        >
                          <option value="all">Tous</option>
                          <option value="duplicates">Doublons</option>
                          <option value="bounced">Bounces</option>
                          <option value="missing">Emails manquants</option>
                          <option value="unsubscribed">Désabonnés</option>
                          <option value="paused">Newsletters en pause</option>
                          <option value="awaiting">Confirmation en cours</option>
                        </select>
                      </div>
                    </label>
                  </div>

                  <div className="app-catalog-advanced-footer is-actions-only">
                    <button
                      type="button"
                      className="button app-catalog-secondary-button"
                      onClick={() => setFilters(createDefaultContactsFilters())}
                      disabled={activeFiltersCount === 0}
                    >
                      <IconLabel icon="x" label="Réinitialiser" />
                    </button>
                  </div>
                </div>
              ) : null}

              <div className="app-catalog-meta">
                <div className="app-catalog-meta-main">
                  <strong>
                    {filteredContacts.length} contact{filteredContacts.length > 1 ? "s" : ""}
                  </strong>
                  {activeFiltersCount > 0 ? (
                    <span className="app-catalog-meta-detail">
                      {activeFiltersCount} filtre{activeFiltersCount > 1 ? "s" : ""} actif{activeFiltersCount > 1 ? "s" : ""}
                    </span>
                  ) : null}
                </div>
                <button
                  type="button"
                  className="app-catalog-refresh"
                  aria-label="Rafraîchir les contacts"
                  title="Rafraîchir les contacts"
                  onClick={() => void contactsQuery.refetch()}
                >
                  <ContactsIcon name="refresh" className="ui-icon" />
                </button>
              </div>

              {activeFilterChips.length > 0 ? (
                <div className="app-active-filter-chips" aria-label="Filtres actifs">
                  {activeFilterChips.map((chip) => (
                    <button
                      key={chip.key}
                      type="button"
                      className="app-active-filter-chip"
                      onClick={() => clearFilterChip(chip.key)}
                    >
                      <span>{chip.label}</span>
                      <ContactsIcon name="x" className="ui-icon" />
                    </button>
                  ))}
                </div>
              ) : null}
            </div>

            {selectedContactsCount > 0 ? (
              <AppSecondaryBar>
                <button
                  type="button"
                  className="button"
                  disabled={selectedContactsCount === 0 || groups.length === 0}
                  onClick={() => openBulkGroupModal("existing")}
                >
                  <IconLabel icon="folder" label={`Ajouter au groupe${selectedContactsCount > 0 ? ` (${selectedContactsCount})` : ""}`} />
                </button>
                <button
                  type="button"
                  className="button"
                  disabled={selectedContactsCount === 0}
                  onClick={() => openBulkGroupModal("new")}
                >
                  <IconLabel icon="plus" label="Créer un groupe et ajouter" />
                </button>
                <button
                  type="button"
                  className="button button-danger"
                  disabled={selectedContactsCount === 0 || bulkDeleteMutation.isPending || assignContactsToGroupMutation.isPending}
                  onClick={() => {
                    if (selectedContactsCount === 0) {
                      return;
                    }
                    const confirmed = window.confirm(
                      selectedContactsCount === 1
                        ? "Supprimer ce contact ?"
                        : `Supprimer ${selectedContactsCount} contacts ?`
                    );
                    if (!confirmed) {
                      return;
                    }
                    bulkDeleteMutation.mutate();
                  }}
                >
                  <IconLabel icon="trash" label={bulkDeleteMutation.isPending ? "Suppression..." : "Supprimer"} />
                </button>
                <span className="muted contacts-selection-hint">
                  {selectedContactsCount} contact{selectedContactsCount > 1 ? "s" : ""} sélectionné
                  {selectedContactsCount > 1 ? "s" : ""}.
                </span>
              </AppSecondaryBar>
            ) : null}

            {contactsQuery.isLoading ? <p className="status-line">Chargement des contacts en cours...</p> : null}
            <p className={statusClassName(collectionStatus)}>{collectionStatus.message}</p>

            {showContactsBlankState ? (
              <div className="app-empty-state contacts-module-empty-state">
                <div className="app-empty-state-visual">
                  <ContactsIcon name="users" size={36} />
                </div>
                <div className="app-empty-state-copy">
                  <h3>Commencez votre audience</h3>
                  <p>Importez un fichier pour créer votre base, ou ajoutez un premier contact à la main.</p>
                </div>
                <div className="app-empty-state-actions">
                  <button type="button" className="button button-primary" onClick={() => setIsImportModalOpen(true)}>
                    <IconLabel icon="upload" label="Importer des contacts" />
                  </button>
                  <button
                    type="button"
                    className="button"
                    onClick={() => startTransition(() => navigate(routes.contactCreatePath))}
                  >
                    <IconLabel icon="plus" label="Créer un contact" />
                  </button>
                </div>
              </div>
            ) : filteredContacts.length === 0 && !contactsQuery.isLoading ? (
              <div className="contacts-empty-state">
                <p className="section-kicker">Aucun contact</p>
                <h3>{contacts.length === 0 ? "Commencez votre base" : "Aucun résultat pour ces filtres"}</h3>
                <p className="muted">
                  {contacts.length === 0
                    ? "Importez un fichier ou créez votre premier contact à la main."
                    : "Essayez une recherche plus large ou réinitialisez les filtres."}
                </p>
              </div>
            ) : null}

            {filteredContacts.length > 0 ? (
              <div className="table-wrap event-table-wrap contacts-table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th className="table-select-col">
                        <SelectionCheckbox
                          checked={allFilteredSelected}
                          indeterminate={!allFilteredSelected && someFilteredSelected}
                          ariaLabel="Sélectionner tous les contacts filtrés"
                          onChange={toggleSelectAllFilteredContacts}
                        />
                      </th>
                      <th>Contact</th>
                      <th>Email</th>
                      <th>Coordonnees</th>
                      <th>Groupes</th>
                      <th>Source</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredContacts.map((contact) => {
                      const normalizedEmail = contact.email.trim().toLowerCase();
                      const hasDuplicateEmail = normalizedEmail.length > 0 && duplicateEmails.has(normalizedEmail);

                      return (
                        <tr
                          key={contact.id}
                          className={selectedContactIds.includes(contact.id) ? "row-bulk-selected" : ""}
                          onClick={() => startTransition(() => navigate(routes.contactDetailPath(contact.id)))}
                        >
                          <td className="table-select-col" onClick={(event) => event.stopPropagation()}>
                            <SelectionCheckbox
                              checked={selectedContactIds.includes(contact.id)}
                              ariaLabel={`Sélectionner ${formatContactName(contact)}`}
                              onChange={(checked) => toggleContactSelection(contact.id, checked)}
                            />
                          </td>
                          <td>
                            <div className="contacts-table-name">
                              <strong>{formatContactName(contact)}</strong>
                              <span className="table-subtext">{contact.company || "Sans société"}</span>
                            </div>
                          </td>
                          <td>
                            <div className="contacts-table-email">
                              <span>{contact.email || "-"}</span>
                              {contact.has_bounced ? <span className="contacts-bounce-pill">Bounce</span> : null}
                              {contact.newsletter_state === "paused" ? <span className="contacts-bounce-pill">Newsletters en pause</span> : null}
                              {["preparing","awaiting"].includes(contact.newsletter_state || "") ? <span className="contacts-bounce-pill">Confirmation en cours</span> : null}
                              {hasDuplicateEmail ? <span className="contacts-duplicate-pill">Doublon</span> : null}
                            </div>
                          </td>
                          <td>
                            <div className="table-cell-stack contacts-table-coordinates">
                              <span>{contact.phone || "-"}</span>
                              <span>{contact.city || "Ville non renseignee"}</span>
                              <span className="table-subtext">
                                {contact.address || `Mis à jour ${contact.updated_at ? contact.updated_at.replace("T", " ") : "-"}`}
                              </span>
                            </div>
                          </td>
                          <td>
                            {contact.groups.length > 0 ? (
                              <div className="contacts-group-cluster">
                                {contact.groups.map((group) => (
                                  <span key={group.id} className="contacts-group-pill">
                                    <span className="contacts-group-dot" style={{ backgroundColor: group.color }} />
                                    {group.name}
                                  </span>
                                ))}
                              </div>
                            ) : (
                              <span className="muted">Aucun groupe</span>
                            )}
                          </td>
                          <td>{formatContactSource(contact.source)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : null}
          </AppSurface>

          <ModalShell
            isOpen={isImportModalOpen}
            title="Importer des contacts"
            subtitle="Ajoutez un fichier CSV, XLSX ou VCF, vérifiez les colonnes puis importez les lignes valides."
            onClose={() => {
              if (importMutation.isPending || isParsingImport) {
                return;
              }
              setIsImportModalOpen(false);
            }}
          >
            <p className="muted">Pour vos newsletters, utilisez une liste de personnes qui attendent vos messages.</p>
            <div className="import-template-bar">
              <div className="import-template-card">
                <strong>Modèle d’import</strong>
                <p className="muted">Téléchargez un fichier prêt à remplir avec les bonnes colonnes et une ligne d’exemple.</p>
              </div>
              <div className="import-actions">
                <button type="button" className="button" onClick={() => void downloadImportTemplate("csv")}>Modèle CSV</button>
                <button type="button" className="button" onClick={() => void downloadImportTemplate("xlsx")}>Modèle XLSX</button>
              </div>
            </div>
            <div className="import-dropzone">
              <div className="import-file-field">
                <span className="import-file-label">Fichier à analyser</span>
                <div className="import-file-picker">
                  <button type="button" className="button button-primary" disabled={isParsingImport || importMutation.isPending} onClick={() => importInputRef.current?.click()}>
                    <IconLabel icon="upload" label={isParsingImport ? "Analyse en cours…" : "Choisir un fichier"} />
                  </button>
                  <span className={`import-file-name${importFileName ? " has-file" : ""}`}>{importFileName || "Aucun fichier sélectionné"}</span>
                </div>
              </div>
              <p className="event-form-help">Le format CSV, XLSX ou VCF est détecté automatiquement. Le VCF passe directement à l’aperçu.</p>
              <p className="event-form-help">L’import crée ou met à jour les contacts selon leur adresse email. Colonnes recommandées : prénom, nom, email, téléphone, entreprise, adresse, ville, groupes et notes.</p>
            </div>

            {isParsingImport ? <p className="status-line">Lecture du fichier...</p> : null}

            {importMappingError ? <p role="alert" className="status-line error">{importMappingError}</p> : null}
            {importPreview?.columns && !isMappingConfirmed ? (
              <ImportColumnMapping key={importFileName + String(importFile?.lastModified)} columns={importPreview.columns} fields={importFields} busy={isParsingImport} onApply={applyImportMapping} />
            ) : null}
            {importPreview?.columns && isMappingConfirmed ? (
              <button type="button" className="button" disabled={isParsingImport || importMutation.isPending} onClick={() => setIsMappingConfirmed(false)}>Modifier les correspondances</button>
            ) : null}

            {importPreview && isMappingConfirmed ? (
              <>
                <div className="contacts-import-summary">
                  <strong>{importPreview.readyCount} prêtes</strong>
                  <strong>{importPreview.createCount} créations</strong>
                  <strong>{importPreview.updateCount} mises à jour</strong>
                  <strong>{importPreview.issueCount} alertes</strong>
                </div>

                <p className="muted">
                  Source {importPreview.source.toUpperCase()} · {importPreview.sheetName}
                </p>

                <div className="contacts-import-table">
                  <table>
                    <thead>
                      <tr>
                        <th>Ligne</th>
                        <th>Mode</th>
                        <th>Contact</th>
                        <th>Groupes</th>
                        <th>Alertes</th>
                      </tr>
                    </thead>
                    <tbody>
                      {importPreview.entries.map((entry) => (
                        <tr key={`${entry.rowNumber}-${entry.form.email || entry.form.first_name}`}>
                          <td>{entry.rowNumber}</td>
                          <td>{entry.mode === "update" ? "Mise à jour" : "Création"}</td>
                          <td>
                            <div className="table-cell-stack">
                              <strong>{[entry.form.first_name, entry.form.last_name].filter(Boolean).join(" ") || entry.form.email}</strong>
                              <span className="table-subtext">{entry.form.email || "-"}</span>
                            </div>
                          </td>
                          <td>{entry.groups.length > 0 ? entry.groups.join(", ") : "-"}</td>
                          <td>
                            {entry.issues.length > 0 ? (
                              <div className="contacts-import-issues">
                                {entry.issues.map((issue) => (
                                  <span key={issue}>{issue}</span>
                                ))}
                              </div>
                            ) : (
                              <span className="muted">Prêt</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="event-summary-actions" style={{ marginTop: 18 }}>
                  <button type="button" className="button button-primary" disabled={importMutation.isPending || isParsingImport || !importPreview.readyCount} onClick={() => importMutation.mutate()}>
                    {importMutation.isPending ? "Import en cours..." : "Importer les lignes valides"}
                  </button>
                  <button type="button" className="button" onClick={() => setImportPreview(null)} disabled={importMutation.isPending}>
                    Réinitialiser l'aperçu
                  </button>
                </div>
              </>
            ) : !importPreview ? (
              <div className="contacts-audience-note">
                <p>Chargez un fichier pour prévisualiser les créations, mises à jour et alertes avant import.</p>
              </div>
            ) : null}
          </ModalShell>

          <ModalShell
            isOpen={isBulkGroupModalOpen}
            title="Ajouter la sélection à un groupe"
            subtitle={`${selectedContactsCount} contact${selectedContactsCount > 1 ? "s" : ""} concerne${selectedContactsCount > 1 ? "s" : ""}`}
            onClose={() => {
              if (assignContactsToGroupMutation.isPending) {
                return;
              }
              setIsBulkGroupModalOpen(false);
            }}
          >
            <div className="contacts-bulk-modal">
              <div className="contacts-bulk-mode-switch" role="group" aria-label="Choix du mode de rattachement">
                <button
                  type="button"
                  className={`event-view-toggle-button${bulkGroupMode === "existing" ? " is-active" : ""}`}
                  onClick={() => setBulkGroupMode("existing")}
                >
                  Groupe existant
                </button>
                <button
                  type="button"
                  className={`event-view-toggle-button${bulkGroupMode === "new" ? " is-active" : ""}`}
                  onClick={() => setBulkGroupMode("new")}
                >
                  Nouveau groupe
                </button>
              </div>

              {bulkGroupMode === "existing" ? (
                <label>
                  Groupe
                  <select
                    value={selectedGroupId}
                    className={hasFieldError(bulkGroupFieldErrors, "name") ? "is-field-invalid" : ""}
                    onChange={(event) => setSelectedGroupId(event.target.value)}
                  >
                    <option value="all" disabled={true}>
                      Choisir un groupe
                    </option>
                    {groups.map((group) => (
                      <option key={group.id} value={group.id}>
                        {group.name} ({group.contact_count})
                      </option>
                    ))}
                  </select>
                </label>
              ) : (
                <>
                  <div className="grid grid-2">
                    <label>
                      Nom du groupe
                      <input
                        value={newGroupForm.name}
                        className={hasFieldError(bulkGroupFieldErrors, "name") ? "is-field-invalid" : ""}
                        onChange={(event) => setNewGroupForm((current) => ({ ...current, name: event.target.value }))}
                      />
                    </label>
                    <label>
                      Couleur
                      <input
                        type="color"
                        value={newGroupForm.color}
                        onChange={(event) => setNewGroupForm((current) => ({ ...current, color: event.target.value }))}
                      />
                    </label>
                  </div>

                  <label>
                    Description
                    <textarea
                      rows={4}
                      value={newGroupForm.description}
                      onChange={(event) => setNewGroupForm((current) => ({ ...current, description: event.target.value }))}
                    />
                  </label>
                </>
              )}

              <div className="contacts-bulk-summary">
                <strong>Contacts sélectionnés</strong>
                <div className="contacts-inline-list">
                  {selectedContacts.slice(0, 8).map((contact) => (
                    <span key={contact.id} className="contacts-inline-pill">
                      {formatContactName(contact)}
                    </span>
                  ))}
                  {selectedContactsCount > 8 ? (
                    <span className="contacts-inline-pill">+{selectedContactsCount - 8} autres</span>
                  ) : null}
                </div>
              </div>

              <p className={statusClassName(bulkGroupStatus)}>{bulkGroupStatus.message}</p>

              <div className="event-summary-actions">
                <button
                  type="button"
                  className="button button-primary"
                  disabled={
                    assignContactsToGroupMutation.isPending ||
                    selectedContactsCount === 0 ||
                    (bulkGroupMode === "existing" ? selectedGroupId === "all" : !newGroupForm.name.trim())
                  }
                  onClick={() => assignContactsToGroupMutation.mutate()}
                >
                  {assignContactsToGroupMutation.isPending ? "Ajout en cours..." : "Ajouter au groupe"}
                </button>
                <button
                  type="button"
                  className="button"
                  disabled={assignContactsToGroupMutation.isPending}
                  onClick={() => setIsBulkGroupModalOpen(false)}
                >
                  Annuler
                </button>
              </div>
            </div>
          </ModalShell>
        </>
      )}
    </ContactsLayoutFrame>
  );
}

function GroupsListPage() {
  const routes = useContactsRoutes();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const initialViewState = readSessionJson<GroupsListViewState>(GROUPS_LIST_VIEW_STATE_STORAGE_KEY, {
    filters: createDefaultGroupFilters(),
    isFiltersPanelOpen: false,
    selectedGroupIds: [],
  });
  const { groupsQuery, contactsQuery, groups } = useContactsData();
  const [filters, setFilters] = useState<GroupFilters>(initialViewState.filters);
  const [collectionStatus, setCollectionStatus] = useState<StatusState>({ message: "", tone: "neutral" });
  const [selectedGroupIds, setSelectedGroupIds] = useState<number[]>(initialViewState.selectedGroupIds ?? []);
  const [isMergeGroupsModalOpen, setIsMergeGroupsModalOpen] = useState(false);
  const [mergeTargetGroupId, setMergeTargetGroupId] = useState("");
  const deferredSearch = useDeferredValue(filters.search);
  const filteredGroups = useMemo(
    () => groups.filter((group) => groupMatchesFilters(group, { search: deferredSearch })),
    [groups, deferredSearch]
  );
  const filteredGroupIds = useMemo(() => filteredGroups.map((group) => group.id), [filteredGroups]);
  const selectedGroups = useMemo(
    () => groups.filter((group) => selectedGroupIds.includes(group.id)),
    [groups, selectedGroupIds]
  );
  const selectedGroupsCount = selectedGroups.length;
  const activeFiltersCount = useMemo(() => countActiveGroupFilters(filters), [filters]);
  const activeGroupFilterChips = useMemo(
    () => (filters.search.trim() ? [{ key: "search", label: `Recherche: ${filters.search.trim()}` }] : []),
    [filters.search]
  );
  const allFilteredGroupsSelected =
    filteredGroupIds.length > 0 && filteredGroupIds.every((groupId) => selectedGroupIds.includes(groupId));
  const someFilteredGroupsSelected = filteredGroupIds.some((groupId) => selectedGroupIds.includes(groupId));

  useEffect(() => {
    const knownGroupIds = new Set(groups.map((group) => group.id));
    setSelectedGroupIds((current) => current.filter((groupId) => knownGroupIds.has(groupId)));
  }, [groups]);

  useEffect(() => {
    if (selectedGroups.length < 2) {
      setIsMergeGroupsModalOpen(false);
    }
    if (!selectedGroups.some((group) => String(group.id) === mergeTargetGroupId)) {
      setMergeTargetGroupId(selectedGroups[0] ? String(selectedGroups[0].id) : "");
    }
  }, [mergeTargetGroupId, selectedGroups]);

  useEffect(() => {
    writeSessionJson(GROUPS_LIST_VIEW_STATE_STORAGE_KEY, {
      filters,
      isFiltersPanelOpen: false,
      selectedGroupIds,
    } satisfies GroupsListViewState);
  }, [filters, selectedGroupIds]);

  const mergeGroupsMutation = useMutation({
    mutationFn: async () => {
      const normalizedTargetGroupId = Number(mergeTargetGroupId);
      if (selectedGroupsCount < 2) {
        throw new Error("Sélectionnez au moins deux groupes.");
      }
      if (!normalizedTargetGroupId) {
        throw new Error("Choisissez le groupe cible à conserver.");
      }
      return fetchJson<GroupRecord>(apiUrl("contacts/groups/merge"), {
        method: "POST",
        body: JSON.stringify({
          source_group_ids: selectedGroupIds.filter((groupId) => groupId !== normalizedTargetGroupId),
          target_group_id: normalizedTargetGroupId,
        }),
      });
    },
    onMutate: () => {
      setCollectionStatus({ message: "Fusion des groupes en cours...", tone: "neutral" });
    },
    onSuccess: async (mergedGroup) => {
      setIsMergeGroupsModalOpen(false);
      setSelectedGroupIds([]);
      setMergeTargetGroupId("");
      setCollectionStatus({
        message: `${selectedGroupsCount} groupes fusionnés dans ${mergedGroup.name}.`,
        tone: "success",
      });
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["contacts-groups"] }),
        queryClient.invalidateQueries({ queryKey: ["contacts-contacts"] }),
      ]);
      publishContactsWorkspaceSync("groups-merged", [mergedGroup]);
    },
    onError: (error) => {
      const normalizedError = normalizeApiError(error);
      setCollectionStatus({ message: normalizedError.message, tone: "error" });
    },
  });

  function toggleGroupSelection(groupId: number, checked: boolean) {
    setSelectedGroupIds((current) => {
      if (checked) {
        return current.includes(groupId) ? current : [...current, groupId];
      }
      return current.filter((value) => value !== groupId);
    });
  }

  function toggleSelectAllFilteredGroups(checked: boolean) {
    setSelectedGroupIds((current) => {
      if (checked) {
        return Array.from(new Set([...current, ...filteredGroupIds]));
      }
      const filteredSet = new Set(filteredGroupIds);
      return current.filter((groupId) => !filteredSet.has(groupId));
    });
  }

  function openMergeGroupsModal() {
    if (selectedGroups.length < 2) {
      return;
    }
    setMergeTargetGroupId(String(selectedGroups[0]?.id ?? ""));
    setIsMergeGroupsModalOpen(true);
  }

  const error =
    groupsQuery.error instanceof Error
      ? groupsQuery.error
      : contactsQuery.error instanceof Error
        ? contactsQuery.error
        : null;

  return (
    <ContactsLayoutFrame
      subtitle="Segmentez vos contacts pour cibler plus vite vos envois newsletter."
      catalogActions={<div className="app-catalog-actions">
                  <button type="button" className="button app-catalog-secondary-button" onClick={() => startTransition(() => navigate(routes.contactCreatePath))}>
                    <IconLabel icon="plus" label="Nouveau contact" />
                  </button>
                  <button type="button" className="button button-primary" onClick={() => startTransition(() => navigate(routes.groupCreatePath))}>
                    <IconLabel icon="folder" label="Nouveau groupe" />
                  </button>
                </div>}
    >
      {error ? (
        <PanelMessage title="Chargement impossible" message={error.message} />
      ) : (
        <>
          <AppSurface className="event-list-panel">
            <div className="app-catalog-toolbar">
              <div className="app-catalog-toolbar-main">
                <div className="app-catalog-search-shell contacts-groups-search-shell">
                  <div className="app-catalog-searchbar">
                    <ContactsIcon name="search" className="ui-icon" />
                    <input
                      type="search"
                      value={filters.search}
                      placeholder="Nom du groupe, description, membre..."
                      aria-label="Rechercher un groupe"
                      onChange={(event) => {
                        const value = event.currentTarget.value;
                        setFilters((current) => ({ ...current, search: value }));
                      }}
                    />
                    {filters.search.trim() ? (
                      <button
                        type="button"
                        className="app-catalog-clear-search"
                        aria-label="Effacer la recherche"
                        title="Effacer la recherche"
                        onClick={() => setFilters((current) => ({ ...current, search: "" }))}
                      >
                        <ContactsIcon name="x" className="ui-icon" />
                      </button>
                    ) : null}
                  </div>

                  <div className="app-catalog-inline-field contacts-groups-inline-field">
                    <span className="app-catalog-inline-label">
                      <ContactsIcon name="users" className="ui-icon" />
                      Segments newsletter
                    </span>
                    <span className="contacts-inline-caption">Listes réutilisables pour les campagnes et les imports.</span>
                  </div>
                </div>


              </div>

              <div className="app-catalog-meta">
                <div className="app-catalog-meta-main">
                  <strong>
                    {filteredGroups.length} groupe{filteredGroups.length > 1 ? "s" : ""}
                  </strong>
                  {activeFiltersCount > 0 ? (
                    <span className="app-catalog-meta-detail">Recherche active</span>
                  ) : null}
                </div>
                <button
                  type="button"
                  className="app-catalog-refresh"
                  aria-label="Rafraîchir les groupes"
                  title="Rafraîchir les groupes"
                  onClick={() => void groupsQuery.refetch()}
                >
                  <ContactsIcon name="refresh" className="ui-icon" />
                </button>
              </div>

              {activeGroupFilterChips.length > 0 ? (
                <div className="app-active-filter-chips" aria-label="Recherche active">
                  {activeGroupFilterChips.map((chip) => (
                    <button
                      key={chip.key}
                      type="button"
                      className="app-active-filter-chip"
                      onClick={() => setFilters(createDefaultGroupFilters())}
                    >
                      <span>{chip.label}</span>
                      <ContactsIcon name="x" className="ui-icon" />
                    </button>
                  ))}
                </div>
              ) : null}
            </div>

            {selectedGroupsCount >= 2 ? (
              <AppSecondaryBar>
                <button
                  type="button"
                  className="button"
                  disabled={mergeGroupsMutation.isPending}
                  onClick={openMergeGroupsModal}
                >
                  <IconLabel icon="folder" label={`Fusionner les groupes (${selectedGroupsCount})`} />
                </button>
                <span className="muted contacts-selection-hint">
                  {selectedGroupsCount} groupes sélectionnés. Choisissez le groupe à conserver pendant la fusion.
                </span>
              </AppSecondaryBar>
            ) : null}

            {groupsQuery.isLoading ? <p className="status-line">Chargement des groupes en cours...</p> : null}
            <p className={statusClassName(collectionStatus)}>{collectionStatus.message}</p>

            {filteredGroups.length === 0 && !groupsQuery.isLoading ? (
              <div className="contacts-empty-state">
                <p className="section-kicker">Aucun groupe</p>
                <h3>{groups.length === 0 ? "Créez votre première audience" : "Aucun groupe ne correspond"}</h3>
                <p className="muted">
                  {groups.length === 0
                    ? "Les groupes servent de cibles directement réutilisables dans la newsletter."
                    : "Essayez une recherche plus large."}
                </p>
              </div>
            ) : null}

            {filteredGroups.length > 0 ? (
              <div className="table-wrap event-table-wrap contacts-table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th className="table-select-col">
                        <SelectionCheckbox
                          checked={allFilteredGroupsSelected}
                          indeterminate={!allFilteredGroupsSelected && someFilteredGroupsSelected}
                          ariaLabel="Sélectionner tous les groupes filtrés"
                          onChange={toggleSelectAllFilteredGroups}
                        />
                      </th>
                      <th>Nom</th>
                      <th>Description</th>
                      <th>Membres</th>
                      <th>Couleur</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredGroups.map((group) => (
                      <tr
                        key={group.id}
                        className={selectedGroupIds.includes(group.id) ? "row-bulk-selected" : ""}
                        onClick={() => startTransition(() => navigate(routes.groupDetailPath(group.id)))}
                      >
                        <td className="table-select-col" onClick={(event) => event.stopPropagation()}>
                          <SelectionCheckbox
                            checked={selectedGroupIds.includes(group.id)}
                            ariaLabel={`Sélectionner ${group.name}`}
                            onChange={(checked) => toggleGroupSelection(group.id, checked)}
                          />
                        </td>
                        <td>
                          <strong>{group.name}</strong>
                        </td>
                        <td>
                          <span className="muted">{group.description || "Aucune description"}</span>
                        </td>
                        <td>{group.contact_count} membre{group.contact_count > 1 ? "s" : ""}</td>
                        <td>
                          <span className="contacts-group-dot" style={{ backgroundColor: group.color }} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : null}
          </AppSurface>

          <ModalShell
            isOpen={isMergeGroupsModalOpen}
            title="Fusionner les groupes"
            subtitle={`${selectedGroupsCount} groupes concernés`}
            onClose={() => {
              if (mergeGroupsMutation.isPending) {
                return;
              }
              setIsMergeGroupsModalOpen(false);
            }}
          >
            <div className="contacts-bulk-modal">
              <label>
                Groupe à conserver
                <select value={mergeTargetGroupId} onChange={(event) => setMergeTargetGroupId(event.target.value)}>
                  <option value="" disabled={true}>
                    Choisir un groupe
                  </option>
                  {selectedGroups.map((group) => (
                    <option key={group.id} value={group.id}>
                      {group.name} ({group.contact_count} membre{group.contact_count > 1 ? "s" : ""})
                    </option>
                  ))}
                </select>
              </label>

              <div className="contacts-bulk-summary">
                <strong>Groupes sélectionnés</strong>
                <div className="contacts-inline-list">
                  {selectedGroups.map((group) => (
                    <span key={group.id} className="contacts-inline-pill">
                      {group.name}
                    </span>
                  ))}
                </div>
              </div>

              <p className="muted">
                Les membres de tous les groupes sélectionnés seront réunis dans le groupe cible, puis les autres groupes seront supprimés.
              </p>
              <p className={statusClassName(collectionStatus)}>{mergeGroupsMutation.isError ? collectionStatus.message : ""}</p>

              <div className="event-summary-actions">
                <button
                  type="button"
                  className="button button-primary"
                  disabled={mergeGroupsMutation.isPending || selectedGroupsCount < 2 || !mergeTargetGroupId}
                  onClick={() => mergeGroupsMutation.mutate()}
                >
                  {mergeGroupsMutation.isPending ? "Fusion en cours..." : "Confirmer la fusion"}
                </button>
                <button
                  type="button"
                  className="button"
                  disabled={mergeGroupsMutation.isPending}
                  onClick={() => setIsMergeGroupsModalOpen(false)}
                >
                  Annuler
                </button>
              </div>
            </div>
          </ModalShell>
        </>
      )}
    </ContactsLayoutFrame>
  );
}

function ContactEditorPage({ mode }: { mode: "create" | "detail" }) {
  const routes = useContactsRoutes();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { contactId } = useParams();
  const { contactsQuery, groupsQuery, contacts, groups } = useContactsData();
  const [form, setForm] = useState(createEmptyContactForm());
  const [fieldErrors, setFieldErrors] = useState<string[]>([]);
  const [status, setStatus] = useState<StatusState>({ message: "", tone: "neutral" });
  const hydratedRef = useRef<string>("");
  const editorViewStateStorageKey =
    mode === "create"
      ? "easy-contacts:contact-editor:create"
      : Number(contactId) > 0
        ? `easy-contacts:contact-editor:${contactId}`
        : "easy-contacts:contact-editor:detail";
  const readEditorViewState = () =>
    readSessionJson<ContactEditorViewState>(editorViewStateStorageKey, {
      isEditingDetail: mode === "create",
    });
  const [isEditingDetail, setIsEditingDetail] = useState(() => readEditorViewState().isEditingDetail);
  const contact = useMemo(
    () => contacts.find((item) => item.id === Number(contactId || "0")) || null,
    [contacts, contactId]
  );
  const selectedGroups = useMemo(
    () => groups.filter((group) => form.group_ids.includes(group.id)),
    [groups, form.group_ids]
  );
  const isEditingForm = mode === "create" || isEditingDetail;

  useEffect(() => {
    if (mode === "create") {
      if (hydratedRef.current !== editorViewStateStorageKey) {
        hydratedRef.current = editorViewStateStorageKey;
        setIsEditingDetail(readEditorViewState().isEditingDetail);
        setForm(createEmptyContactForm());
        setFieldErrors([]);
        setStatus({ message: "", tone: "neutral" });
      }
      return;
    }

    if (contact && hydratedRef.current !== editorViewStateStorageKey) {
      hydratedRef.current = editorViewStateStorageKey;
      setIsEditingDetail(readEditorViewState().isEditingDetail);
      setForm(toContactForm(contact));
      setFieldErrors([]);
      setStatus({ message: "", tone: "neutral" });
    }
  }, [contact, editorViewStateStorageKey, mode]);

  useEffect(() => {
    writeSessionJson(editorViewStateStorageKey, {
      isEditingDetail,
    } satisfies ContactEditorViewState);
  }, [editorViewStateStorageKey, isEditingDetail]);

  const saveMutation = useMutation({
    mutationFn: (payload: Record<string, unknown>) =>
      fetchJson<ContactRecord>(apiUrl("contacts"), {
        method: "POST",
        body: JSON.stringify(payload),
      }),
    onSuccess: async (savedContact) => {
      setForm(toContactForm(savedContact));
      setIsEditingDetail(false);
      setFieldErrors([]);
      setStatus({ message: "Contact enregistre.", tone: "success" });
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["contacts-contacts"] }),
        queryClient.invalidateQueries({ queryKey: ["contacts-groups"] }),
        queryClient.invalidateQueries({ queryKey: ["contacts-meta"] }),
      ]);
      publishContactsWorkspaceSync("contact-saved");
      if (mode === "create") {
        startTransition(() => navigate(routes.contactDetailPath(savedContact.id), { replace: true }));
      }
    },
    onError: (error) => {
      const normalizedError = normalizeApiError(error);
      setFieldErrors(resolveFieldErrors(normalizedError, inferContactFieldErrors(normalizedError.message)));
      setStatus({ message: normalizedError.message, tone: "error" });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () =>
      fetchJson<{ deleted_count: number }>(apiUrl("contacts/delete-bulk"), {
        method: "POST",
        body: JSON.stringify({ contact_ids: contact ? [contact.id] : [] }),
      }),
    onMutate: () => {
      setStatus({ message: "Suppression du contact en cours...", tone: "neutral" });
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["contacts-contacts"] }),
        queryClient.invalidateQueries({ queryKey: ["contacts-groups"] }),
        queryClient.invalidateQueries({ queryKey: ["contacts-meta"] }),
      ]);
      publishContactsWorkspaceSync("contact-deleted");
      startTransition(() => navigate(routes.listPath, { replace: true }));
    },
    onError: (error) => {
      const normalizedError = normalizeApiError(error);
      setStatus({ message: normalizedError.message, tone: "error" });
    },
  });

  const error =
    contactsQuery.error instanceof Error
      ? contactsQuery.error
      : groupsQuery.error instanceof Error
        ? groupsQuery.error
        : null;

  const isLoading = (mode === "detail" && contactsQuery.isLoading) || groupsQuery.isLoading;

  if (error) {
    return (
      <ContactsLayoutFrame subtitle="Edition d'un contact">
        <PanelMessage title="Chargement impossible" message={error.message} />
      </ContactsLayoutFrame>
    );
  }

  if (mode === "detail" && !isLoading && !contact) {
    return (
      <ContactsLayoutFrame subtitle="Edition d'un contact">
        <PanelMessage title="Contact introuvable" message="Ce contact n'existe plus dans la base hosted." />
      </ContactsLayoutFrame>
    );
  }

  return (
    <ContactsLayoutFrame
      subtitle={mode === "create" ? "Ajouter un contact a la base." : contact ? formatContactName(contact) : formatDraftContactName(form)}
      heading={isEditingForm ? (<EditorHeader
                kicker={mode === "create" ? "Creation" : "Fiche contact"}
                title={mode === "create" ? "Nouveau contact" : contact ? formatContactName(contact) : "Modifier le contact"}
                actions={
                  <>
                    <button
                      type="button"
                      className="button"
                      onClick={() => {
                        if (!contact) {
                          startTransition(() => navigate(routes.listPath));
                          return;
                        }
                        setForm(toContactForm(contact));
                        setFieldErrors([]);
                        setStatus({ message: "", tone: "neutral" });
                        setIsEditingDetail(false);
                      }}
                    >
                      Annuler
                    </button>
                    <button
                      type="button"
                      className="button button-primary"
                      disabled={saveMutation.isPending}
                      onClick={() => saveMutation.mutate(buildContactPayload(form))}
                    >
                      {saveMutation.isPending ? "Enregistrement..." : "Enregistrer"}
                    </button>
                  </>
                }
              />) : undefined}
      editorBack={
        <button
          type="button"
          className="button app-toolbar-back-button"
          onClick={() => startTransition(() => navigate(routes.listPath))}
        >
          <IconLabel icon="arrow-left" label="Retour au catalogue" />
        </button>
      }
    >
      <div className={`contacts-detail-grid${isEditingForm ? "" : " contacts-detail-grid-single"}`}>
        <EditorPanel className="contacts-detail-panel">
          {isEditingForm ? (
            <>


              {isLoading ? <p className="status-line">Chargement du contact...</p> : null}
              <p className={statusClassName(status)}>{status.message}</p>

              <h2 className="contacts-form-title">Informations du contact</h2>
              <div className="grid grid-2">
                <label>
                  Prenom
                  <input
                    value={form.first_name}
                    onChange={(event) => setForm((current) => ({ ...current, first_name: event.target.value }))}
                  />
                </label>
                <label>
                  Nom
                  <input
                    value={form.last_name}
                    onChange={(event) => setForm((current) => ({ ...current, last_name: event.target.value }))}
                  />
                </label>
              </div>

              <div className="grid grid-2">
                <label>
                  Email
                  <input
                    value={form.email}
                    className={hasFieldError(fieldErrors, "email") ? "is-field-invalid" : ""}
                    aria-invalid={hasFieldError(fieldErrors, "email") || undefined}
                    onChange={(event) => {
                      setFieldErrors((current) => current.filter((value) => value !== "email"));
                      setForm((current) => ({ ...current, email: event.target.value }));
                    }}
                  />
                </label>
                <label>
                  Telephone
                  <input
                    value={form.phone}
                    onChange={(event) => setForm((current) => ({ ...current, phone: event.target.value }))}
                  />
                </label>
              </div>

              <h2 className="contacts-form-section-title">Informations complémentaires</h2>
              <label>
                Societe
                <input
                  value={form.company}
                  onChange={(event) => setForm((current) => ({ ...current, company: event.target.value }))}
                />
              </label>

              <div className="grid grid-2">
                <label>
                  Ville
                  <input
                    value={form.city}
                    onChange={(event) => setForm((current) => ({ ...current, city: event.target.value }))}
                  />
                </label>
                <label>
                  Adresse
                  <input
                    value={form.address}
                    onChange={(event) => setForm((current) => ({ ...current, address: event.target.value }))}
                  />
                </label>
              </div>

              <label>
                Notes
                <textarea
                  rows={5}
                  value={form.notes}
                  onChange={(event) => setForm((current) => ({ ...current, notes: event.target.value }))}
                ></textarea>
              </label>

            </>
          ) : (
            <>
              <div className="panel-head">
                <div>
                  <p className="section-kicker">Fiche contact</p>
                  <h2>{contact ? formatContactName(contact) : formatDraftContactName(form)}</h2>
                </div>
                <div className="event-toolbar-actions">
                  <button type="button" className="button button-primary" onClick={() => setIsEditingDetail(true)}>
                    <IconLabel icon="edit" label="Modifier" />
                  </button>
                  <button
                    type="button"
                    className="button button-danger"
                    disabled={!contact || deleteMutation.isPending}
                    onClick={() => {
                      if (!contact) {
                        return;
                      }
                      const confirmed = window.confirm("Supprimer ce contact ?");
                      if (!confirmed) {
                        return;
                      }
                      deleteMutation.mutate();
                    }}
                  >
                    <IconLabel icon="trash" label={deleteMutation.isPending ? "Suppression..." : "Supprimer"} />
                  </button>
                </div>
              </div>

              {isLoading ? <p className="status-line">Chargement du contact...</p> : null}
              <p className={statusClassName(status)}>{status.message}</p>

              {contact?.newsletter_state === "paused" ? <p className="status-line">Newsletters en pause. Une réinscription explicite est nécessaire pour reprendre les envois.</p> : null}
              {["preparing","awaiting"].includes(contact?.newsletter_state || "") ? <p className="status-line">{contact?.newsletter_confirmation_deadline_at
                ? `Confirmation d’abonnement attendue avant le ${formatContactBounceDate(contact.newsletter_confirmation_deadline_at)}.`
                : "Une demande de confirmation d’abonnement est en préparation."}</p> : null}
              {contact?.has_bounced ? (
                <div className="contacts-bounce-alert" role="alert">
                  <strong>Adresse rejetée</strong>
                  <span>
                    {contact.bounced_at
                      ? `L'adresse email de ce contact a été rejetée lors d'un envoi newsletter le ${formatContactBounceDate(contact.bounced_at)}.`
                      : "L'adresse email de ce contact a déjà été rejetée lors d'un envoi newsletter."}
                  </span>
                </div>
              ) : null}

              <div className="contacts-detail-meta contacts-readonly-meta">
                <div className="contacts-detail-meta-item">
                  <strong>Prenom</strong>
                  <span>{contact?.first_name || "-"}</span>
                </div>
                <div className="contacts-detail-meta-item">
                  <strong>Nom</strong>
                  <span>{contact?.last_name || "-"}</span>
                </div>
                <div className="contacts-detail-meta-item">
                  <strong>Email</strong>
                  <span>{contact?.email || "-"}</span>
                </div>
                <div className="contacts-detail-meta-item">
                  <strong>Telephone</strong>
                  <span>{contact?.phone || "-"}</span>
                </div>
                <div className="contacts-detail-meta-item">
                  <strong>Societe</strong>
                  <span>{contact?.company || "-"}</span>
                </div>
                <div className="contacts-detail-meta-item">
                  <strong>Ville</strong>
                  <span>{contact?.city || "-"}</span>
                </div>
                <div className="contacts-detail-meta-item">
                  <strong>Adresse</strong>
                  <span>{contact?.address || "-"}</span>
                </div>
                <div className="contacts-detail-meta-item contacts-detail-meta-item-full">
                  <strong>Notes</strong>
                  <span>{contact?.notes || "-"}</span>
                </div>
                <div className="contacts-detail-meta-item contacts-detail-meta-item-full">
                  <strong>Groupes</strong>
                  <div className="contacts-inline-list">
                    {contact?.groups.length ? (
                      contact.groups.map((group) => (
                        <span key={group.id} className="contacts-inline-pill">
                          <span className="contacts-group-dot" style={{ backgroundColor: group.color }} />
                          {group.name}
                        </span>
                      ))
                    ) : (
                      <span className="muted">Aucun groupe</span>
                    )}
                  </div>
                </div>
              </div>
            </>
          )}
        </EditorPanel>
        {isEditingForm ? (
          <EditorPanel className="contacts-editor-groups" label="Groupes">
            <div className="panel-head">
              <div>
                <p className="section-kicker">Rattachement</p>
                <h3>Groupes</h3>
              </div>
            </div>

            {groups.length > 0 ? (
              <div className="contacts-group-selector">
                {groups.map((group) => {
                  const checked = form.group_ids.includes(group.id);
                  return (
                    <label key={group.id} className="contacts-group-selector-card">
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={(event) => {
                          setFieldErrors((current) => current.filter((value) => value !== "group_ids"));
                          setForm((current) => ({
                            ...current,
                            group_ids: event.target.checked
                              ? [...current.group_ids, group.id]
                              : current.group_ids.filter((groupId) => groupId !== group.id),
                          }));
                        }}
                      />
                      <span className="contacts-group-dot" style={{ backgroundColor: group.color }} />
                      <span className="contacts-group-selector-copy">
                        <strong>{group.name}</strong>
                        <span className="muted">
                          {group.contact_count} membre{group.contact_count > 1 ? "s" : ""}
                        </span>
                      </span>
                    </label>
                  );
                })}
              </div>
            ) : (
              <div className="contacts-audience-note">
                <p>Aucun groupe n'existe encore.</p>
                <button type="button" className="button" onClick={() => startTransition(() => navigate(routes.groupCreatePath))}>
                  Créer un groupe
                </button>
              </div>
            )}
          </EditorPanel>
        ) : null}
      </div>
    </ContactsLayoutFrame>
  );
}

function GroupEditorPage({ mode }: { mode: "create" | "detail" }) {
  const routes = useContactsRoutes();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { groupId } = useParams();
  const { groupsQuery, contactsQuery, groups, contacts } = useContactsData();
  const [form, setForm] = useState(createEmptyGroupForm());
  const [fieldErrors, setFieldErrors] = useState<string[]>([]);
  const [status, setStatus] = useState<StatusState>({ message: "", tone: "neutral" });
  const [memberSearch, setMemberSearch] = useState("");
  const hydratedRef = useRef<string>("");
  const deferredMemberSearch = useDeferredValue(memberSearch);
  const group = useMemo(() => groups.find((item) => item.id === Number(groupId || "0")) || null, [groups, groupId]);
  const visibleContacts = useMemo(() => {
    const normalizedSearch = deferredMemberSearch.trim().toLowerCase();
    if (!normalizedSearch) {
      return contacts;
    }
    return contacts.filter((contact) => formatMemberSearch(contact).includes(normalizedSearch));
  }, [contacts, deferredMemberSearch]);
  const selectedMembers = useMemo(
    () => contacts.filter((contact) => form.member_ids.includes(contact.id)),
    [contacts, form.member_ids]
  );
  const editorViewStateStorageKey =
    mode === "create"
      ? "easy-contacts:group-editor:create"
      : Number(groupId) > 0
        ? `easy-contacts:group-editor:${groupId}`
        : "easy-contacts:group-editor:detail";
  const readEditorViewState = () =>
    readSessionJson<GroupEditorViewState>(editorViewStateStorageKey, {
      isEditingDetail: mode === "create",
    });
  const [isEditingDetail, setIsEditingDetail] = useState(() => readEditorViewState().isEditingDetail);
  const isEditingForm = mode === "create" || isEditingDetail;

  useEffect(() => {
    if (mode === "create") {
      if (hydratedRef.current !== editorViewStateStorageKey) {
        hydratedRef.current = editorViewStateStorageKey;
        setIsEditingDetail(readEditorViewState().isEditingDetail);
        setForm(createEmptyGroupForm());
        setFieldErrors([]);
        setMemberSearch("");
        setStatus({ message: "", tone: "neutral" });
      }
      return;
    }

    if (group && hydratedRef.current !== editorViewStateStorageKey) {
      hydratedRef.current = editorViewStateStorageKey;
      setIsEditingDetail(readEditorViewState().isEditingDetail);
      setForm(toGroupForm(group));
      setFieldErrors([]);
      setMemberSearch("");
      setStatus({ message: "", tone: "neutral" });
    }
  }, [editorViewStateStorageKey, group, mode]);

  useEffect(() => {
    writeSessionJson(editorViewStateStorageKey, {
      isEditingDetail,
    } satisfies GroupEditorViewState);
  }, [editorViewStateStorageKey, isEditingDetail]);

  const saveMutation = useMutation({
    mutationFn: (payload: Record<string, unknown>) =>
      fetchJson<GroupRecord>(apiUrl("groups"), {
        method: "POST",
        body: JSON.stringify(payload),
      }),
    onSuccess: async (savedGroup) => {
      setForm(toGroupForm(savedGroup));
      setIsEditingDetail(false);
      setFieldErrors([]);
      setStatus({ message: "Groupe enregistre.", tone: "success" });
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["contacts-contacts"] }),
        queryClient.invalidateQueries({ queryKey: ["contacts-groups"] }),
        queryClient.invalidateQueries({ queryKey: ["contacts-meta"] }),
      ]);
      publishContactsWorkspaceSync("group-saved", [savedGroup]);
      if (mode === "create") {
        startTransition(() => navigate(routes.groupDetailPath(savedGroup.id), { replace: true }));
      }
    },
    onError: (error) => {
      const normalizedError = normalizeApiError(error);
      setFieldErrors(resolveFieldErrors(normalizedError, inferGroupFieldErrors(normalizedError.message)));
      setStatus({ message: normalizedError.message, tone: "error" });
    },
  });

  const error =
    groupsQuery.error instanceof Error
      ? groupsQuery.error
      : contactsQuery.error instanceof Error
        ? contactsQuery.error
        : null;

  const isLoading = (mode === "detail" && groupsQuery.isLoading) || contactsQuery.isLoading;

  if (error) {
    return (
      <ContactsLayoutFrame subtitle="Edition d'un groupe">
        <PanelMessage title="Chargement impossible" message={error.message} />
      </ContactsLayoutFrame>
    );
  }

  if (mode === "detail" && !isLoading && !group) {
    return (
      <ContactsLayoutFrame subtitle="Edition d'un groupe">
        <PanelMessage title="Groupe introuvable" message="Ce groupe n'existe plus dans la base hosted." />
      </ContactsLayoutFrame>
    );
  }

  return (
    <ContactsLayoutFrame
      subtitle={mode === "create" ? "Construire une audience reutilisable dans newsletter." : form.name || "Groupe"}
      heading={isEditingForm ? (<EditorHeader
                kicker={mode === "create" ? "Creation" : "Fiche groupe"}
                title={mode === "create" ? "Nouveau groupe" : "Modifier ce groupe"}
                actions={
                  <>
                    <button
                      type="button"
                      className="button"
                      onClick={() => {
                        if (!group) {
                          startTransition(() => navigate(routes.groupsListPath));
                          return;
                        }
                        setForm(toGroupForm(group));
                        setFieldErrors([]);
                        setStatus({ message: "", tone: "neutral" });
                        setIsEditingDetail(false);
                      }}
                    >
                      Annuler
                    </button>
                    <button
                      type="button"
                      className="button button-primary"
                      disabled={saveMutation.isPending}
                      onClick={() => saveMutation.mutate(buildGroupPayload(form))}
                    >
                      {saveMutation.isPending ? "Enregistrement..." : "Enregistrer"}
                    </button>
                  </>
                }
              />) : undefined}
      editorBack={
        <button type="button" className="button app-toolbar-back-button" onClick={() => startTransition(() => navigate(routes.groupsListPath))}>
          <IconLabel icon="arrow-left" label="Retour au catalogue" />
        </button>
      }
    >
      <div className="contacts-detail-grid contacts-detail-grid-single">
        <EditorPanel className="contacts-detail-panel">
          {isEditingForm ? (
            <>


              {isLoading ? <p className="status-line">Chargement du groupe...</p> : null}
              <p className={statusClassName(status)}>{status.message}</p>

              <div className="grid grid-2">
                <label>
                  Nom du groupe
                  <input
                    value={form.name}
                    className={hasFieldError(fieldErrors, "name") ? "is-field-invalid" : ""}
                    aria-invalid={hasFieldError(fieldErrors, "name") || undefined}
                    onChange={(event) => {
                      setFieldErrors((current) => current.filter((value) => value !== "name"));
                      setForm((current) => ({ ...current, name: event.target.value }));
                    }}
                  />
                </label>
                <label>
                  Couleur
                  <input
                    type="color"
                    value={form.color}
                    onChange={(event) => setForm((current) => ({ ...current, color: event.target.value }))}
                  />
                </label>
              </div>

              <label>
                Description
                <textarea
                  rows={4}
                  value={form.description}
                  onChange={(event) => setForm((current) => ({ ...current, description: event.target.value }))}
                ></textarea>
              </label>

              <div style={{ marginTop: 22 }}>
                <div className="panel-head">
                  <div>
                    <p className="section-kicker">Composition</p>
                    <h3>Membres du groupe</h3>
                  </div>
                </div>

                <div className="contacts-member-selector">
                  <label className="contacts-member-search">
                    Recherche
                    <input
                      value={memberSearch}
                      placeholder="Nom, email, societe..."
                      onChange={(event) => setMemberSearch(event.target.value)}
                    />
                  </label>

                  {contacts.length > 0 ? (
                    <div className="contacts-member-list">
                      {visibleContacts.map((contact) => {
                        const checked = form.member_ids.includes(contact.id);
                        return (
                          <label key={contact.id} className="contacts-member-row">
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={(event) => {
                                setFieldErrors((current) => current.filter((value) => value !== "member_ids"));
                                setForm((current) => ({
                                  ...current,
                                  member_ids: event.target.checked
                                    ? [...current.member_ids, contact.id]
                                    : current.member_ids.filter((contactId) => contactId !== contact.id),
                                }));
                              }}
                            />
                            <span className="contacts-member-selector-copy">
                              <strong>{formatContactName(contact)}</strong>
                              <span>{contact.email}</span>
                              <span>{contact.company || "Sans societe"}</span>
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="contacts-audience-note">
                      <p>Ajoutez d'abord des contacts a la base pour composer ce groupe.</p>
                      <button type="button" className="button" onClick={() => startTransition(() => navigate(routes.contactCreatePath))}>
                        Créer un contact
                      </button>
                    </div>
                  )}
                </div>
              </div>

            </>
          ) : (
            <>
              <div className="panel-head">
                <div>
                  <p className="section-kicker">Fiche groupe</p>
                  <h2>{group ? group.name : form.name || "Groupe"}</h2>
                </div>
                <div className="event-toolbar-actions">
                  <button type="button" className="button button-primary" onClick={() => setIsEditingDetail(true)}>
                    <IconLabel icon="edit" label="Modifier" />
                  </button>
                </div>
              </div>

              {isLoading ? <p className="status-line">Chargement du groupe...</p> : null}
              <p className={statusClassName(status)}>{status.message}</p>

              <div className="contacts-detail-meta contacts-readonly-meta">
                <div className="contacts-detail-meta-item">
                  <strong>Nom</strong>
                  <span>{group?.name || "-"}</span>
                </div>
                <div className="contacts-detail-meta-item">
                  <strong>Couleur</strong>
                  <span>
                    {group?.color ? (
                      <span className="contacts-group-dot" style={{ backgroundColor: group.color }} />
                    ) : "-"}
                  </span>
                </div>
                <div className="contacts-detail-meta-item contacts-detail-meta-item-full">
                  <strong>Description</strong>
                  <span>{group?.description || "-"}</span>
                </div>
              </div>

              <div style={{ marginTop: 22 }}>
                <div className="panel-head">
                  <div>
                    <p className="section-kicker">Composition</p>
                    <h3>Membres ({selectedMembers.length})</h3>
                  </div>
                </div>
                {selectedMembers.length > 0 ? (
                  <div className="contacts-member-list">
                    {selectedMembers.map((member) => (
                      <div key={member.id} className="contacts-member-row">
                        <span className="contacts-member-selector-copy">
                          <strong>{formatContactName(member)}</strong>
                          <span>{member.email}</span>
                          <span>{member.company || "Sans societe"}</span>
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="contacts-audience-note">
                    <p>Aucun membre dans ce groupe.</p>
                  </div>
                )}
              </div>
            </>
          )}
        </EditorPanel>
      </div>
    </ContactsLayoutFrame>
  );
}

export function createContactsRoutes(routes: ContactsRouteConfig = CONTACTS_STANDALONE_ROUTES) {
  return [
    {
      path: routes.listPath,
      element: withContactsRouteConfig(<ContactsListPage />, routes),
    },
    {
      path: routes.contactCreatePath,
      element: withContactsRouteConfig(<ContactEditorPage mode="create" />, routes),
    },
    {
      path: routes.contactDetailPattern,
      element: withContactsRouteConfig(<ContactEditorPage mode="detail" />, routes),
    },
    {
      path: routes.groupsListPath,
      element: withContactsRouteConfig(<GroupsListPage />, routes),
    },
    {
      path: routes.groupCreatePath,
      element: withContactsRouteConfig(<GroupEditorPage mode="create" />, routes),
    },
    {
      path: routes.groupDetailPattern,
      element: withContactsRouteConfig(<GroupEditorPage mode="detail" />, routes),
    },
  ];
}

export function createHostedContactsRoutes() {
  return createContactsRoutes(CONTACTS_HOSTED_ROUTES);
}

const router = createBrowserRouter(createContactsRoutes(), { basename: resolveMountedBasename("contacts") });

export function App() {
  return <RouterProvider router={router} />;
}
